"use client";

import { CardTitle } from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import {
  CatalogMode,
  IHubCatalogStatus as HubCatalogStatusEnum,
  type IHubCatalogStatus,
  type IHubCatalogVisibility,
} from "@enums";
import type { IBadgeStripItem } from "@models";
import { BadgeStrip, CatalogIcon } from "@modules";
import {
  resolveHubCatalogLifecycleBadge,
  resolveHubCatalogTypeBadge,
  resolveHubCatalogVisibilityBadge,
} from "@utils";
import { useMemo } from "react";
import { useSearchParams } from "react-router";
import { CatalogRating } from "./CatalogRating";

interface ICatalogHeaderProps {
  label: string;
  catalogTypeLabel?: string;
  status?: IHubCatalogStatus;
  visibility?: IHubCatalogVisibility;
  supportedCloud?: string[];
  mode?: CatalogMode;
  catalogId?: number;
  icon?: string;
  averageRating?: number;
}

export function CatalogHeader({
  label,
  catalogTypeLabel,
  status,
  visibility,
  supportedCloud = [],
  mode = CatalogMode.Create,
  catalogId,
  icon,
  averageRating,
}: ICatalogHeaderProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const isExplorer = searchParams.get("tab") === "explorer";
  const isSubscriptionsTab = searchParams.get("tab") === "subscriptions";
  const isDeploymentsTab = searchParams.get("fromTab") === "deployments";
  const showRating =
    (mode === CatalogMode.Details ||
      mode === CatalogMode.Edit ||
      mode === CatalogMode.Review) &&
    (catalogId ?? 0) > 0;

  const lifecycleBadge = useMemo(() => {
    const effectiveStatus =
      status ??
      (mode === CatalogMode.Create || mode === CatalogMode.Edit
        ? HubCatalogStatusEnum.Draft
        : undefined);
    return effectiveStatus !== undefined
      ? resolveHubCatalogLifecycleBadge(effectiveStatus, t, visibility)
      : null;
  }, [status, mode, t, visibility]);

  const metaBadgeItems = useMemo((): IBadgeStripItem[] => {
    const items: IBadgeStripItem[] = [];
    const catalogTypeBadge = resolveHubCatalogTypeBadge(catalogTypeLabel);
    if (catalogTypeBadge) {
      items.push(catalogTypeBadge);
    }
    if (visibility) {
      const visibilityBadge = resolveHubCatalogVisibilityBadge(visibility, t);
      if (visibilityBadge) {
        items.push(visibilityBadge);
      }
    }
    if (
      lifecycleBadge &&
      mode !== CatalogMode.Explorer &&
      !isExplorer &&
      !isSubscriptionsTab &&
      !isDeploymentsTab
    ) {
      items.push(lifecycleBadge);
    }
    return items;
  }, [
    catalogTypeLabel,
    visibility,
    lifecycleBadge,
    mode,
    isExplorer,
    isSubscriptionsTab,
    isDeploymentsTab,
  ]);

  const cloudBadgeItems = useMemo(
    (): IBadgeStripItem[] =>
      supportedCloud.map((cloud) => ({
        id: cloud,
        label: cloud,
        variant: "secondary",
        size: "md",
      })),
    [supportedCloud],
  );

  return (
    <div className="flex w-full flex-col gap-3">
      <BadgeStrip className="gap-2.5" items={metaBadgeItems} />
      <div className="flex gap-4 items-center">
        <CatalogIcon icon={icon} alt={label} />
        <CardTitle className="min-w-0 w-full">{label}</CardTitle>
      </div>
      {showRating && <CatalogRating averageRating={averageRating} />}
      <BadgeStrip items={cloudBadgeItems} />
    </div>
  );
}
